import { apiClient } from './client';

export type ReplyClassification = 'interested' | 'not_interested' | 'question' | 'unclear' | 'buying_ready';
export type ThreadKind = 'email' | 'phone' | 'meeting' | 'note';

export interface EmailThread {
  kind?: ThreadKind;
  author?: string | null;
  id: string;
  contact_id: string | null;
  mandate_id: string | null;
  direction: 'sent' | 'received';
  from_email: string;
  to_email: string;
  subject: string;
  body_text: string;
  message_id: string | null;
  in_reply_to: string | null;
  occurred_at: string;
  is_read: boolean;
  attachment_names: string[];
  created_at: string;
  classification: ReplyClassification | null;
  classification_manual: boolean;
  classified_at: string | null;
  reply_to_sending_id: string | null;
  contact_display_name: string | null;
  company_id: string | null;
  company_display_name: string | null;
  mandate_title: string | null;
}

export async function getEmailThreads(params: {
  contact_id?: string;
  mandate_id?: string;
  company_id?: string;
}): Promise<{ data: EmailThread[] }> {
  const { data } = await apiClient.get('/email-threads', { params });
  return data;
}

export interface SyncStatus {
  running: boolean;
  startedAt?: number;
  imported: number;
  skipped: number;
  error: string | null;
  finishedAt: number | null;
}

export async function syncEmailThreads(): Promise<{ started: boolean; alreadyRunning?: boolean; status?: SyncStatus }> {
  const { data } = await apiClient.post('/email-threads/sync');
  return data;
}

export async function getSyncStatus(): Promise<SyncStatus> {
  const { data } = await apiClient.get('/email-threads/sync/status');
  return data;
}

export async function replyEmailThread(payload: {
  contact_id?: string;
  mandate_id?: string;
  to_email: string;
  subject: string;
  body_text: string;
  in_reply_to?: string;
}): Promise<{ data: EmailThread }> {
  const { data } = await apiClient.post('/email-threads/reply', payload);
  return data;
}

export async function markThreadRead(id: string): Promise<void> {
  await apiClient.patch(`/email-threads/${id}/read`);
}

export async function createManualThread(payload: {
  mandate_id?: string;
  contact_id?: string;
  kind: 'phone' | 'meeting' | 'note';
  subject: string;
  body_text?: string;
  occurred_at?: string; // ISO
  author?: string;
}): Promise<{ data: { id: string; occurred_at: string } }> {
  const { data } = await apiClient.post('/email-threads/manual', payload);
  return data;
}

export async function deleteEmailThread(id: string): Promise<void> {
  await apiClient.delete(`/email-threads/${id}`);
}

export async function setThreadClassification(
  id: string,
  classification: ReplyClassification | null,
): Promise<{ data: { id: string; classification: ReplyClassification | null; classification_manual: boolean; classified_at: string | null } }> {
  const { data } = await apiClient.patch(`/email-threads/${id}/classification`, { classification });
  return data;
}
